import { MouseEvent, useMemo } from "react";
import { Dayjs } from "dayjs";
import FormHelperText from "@mui/material/FormHelperText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { IncomeExpenseReportMode } from "../../types/income-expense-report.type";
import { Branch } from "../../types/user.type";
import DateRangePicker, { WITHIN_A_MONTH_PRESET_GROUPS } from "../common/DateRangePicker";
import CodeMultiSelect, { CodeOption } from "../DemandFactor/CodeMultiSelect";
import { ACTUAL_BADGE_TEXT, ESTIMATE_BADGE_TEXT, RANGE_TOO_LONG_TEXT } from "./constants";

export interface ReportFilterValue {
  dateFrom: Dayjs;
  dateTo: Dayjs;
  /** Head office only; none = every branch. */
  branchCodes: string[];
  mode: IncomeExpenseReportMode;
}

const MODE_HINT: Record<IncomeExpenseReportMode, string> = {
  estimate: "ค่าใช้จ่ายฝ่ายขายและบริหารใช้ยอดประมาณการ (Sales / Manager ต่อสายต่อวัน)",
  actual: "ค่าใช้จ่ายใช้ยอดจริงที่อนุมัติแล้ว",
};

const isReportMode = (value: unknown): value is IncomeExpenseReportMode =>
  value === "estimate" || value === "actual";

interface IncomeExpenseFiltersProps {
  value: ReportFilterValue;
  /** Head office and master pick branches; a branch role is pinned to its own. */
  canPickBranch: boolean;
  branches: Branch[];
  isRangeTooLong: boolean;
  onChange: (patch: Partial<ReportFilterValue>) => void;
}

/** ประมาณการ | จริง, วันที่ (≤ 31 days) and, for head office, สาขา (several; none = all). */
export default function IncomeExpenseFilters({
  value,
  canPickBranch,
  branches,
  isRangeTooLong,
  onChange,
}: Readonly<IncomeExpenseFiltersProps>) {
  const branchOptions = useMemo<CodeOption[]>(
    () =>
      branches.map((branch) => ({
        code: branch.branch_code,
        label: `${branch.branch_code} - ${branch.branch_name}`,
      })),
    [branches],
  );
  const handleModeChange = (_event: MouseEvent<HTMLElement>, next: unknown) => {
    if (isReportMode(next)) onChange({ mode: next });
  };
  const handleDateChange = (dateFrom: Dayjs, dateTo: Dayjs) => onChange({ dateFrom, dateTo });
  const handleBranchChange = (branchCodes: string[]) => onChange({ branchCodes });

  return (
    <Paper>
      <Stack direction="row" flexWrap="wrap" alignItems="flex-start" gap={2}>
        <Stack gap={0.5}>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={value.mode}
            onChange={handleModeChange}
            aria-label="โหมดค่าใช้จ่าย"
          >
            <ToggleButton value="estimate">{ESTIMATE_BADGE_TEXT}</ToggleButton>
            <ToggleButton value="actual">{ACTUAL_BADGE_TEXT}</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {MODE_HINT[value.mode]}
          </Typography>
        </Stack>
        <Stack sx={{ minWidth: 280 }}>
          <DateRangePicker
            dateFrom={value.dateFrom}
            dateTo={value.dateTo}
            onChange={handleDateChange}
            presetGroups={WITHIN_A_MONTH_PRESET_GROUPS}
          />
          {isRangeTooLong && <FormHelperText error>{RANGE_TOO_LONG_TEXT}</FormHelperText>}
        </Stack>
        {canPickBranch && (
          <Stack sx={{ flex: 1, minWidth: 320 }}>
            <CodeMultiSelect
              label="สาขา (ไม่เลือก = ทุกสาขา)"
              options={branchOptions}
              value={value.branchCodes}
              onChange={handleBranchChange}
            />
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
